// "Notify me about this sender" (U10): the owner's own change to the VIPs document, saved exactly
// as the VIPs page saves it (same document, same revision check, Undo back to the revision before).
// Only VIP breakthroughs reach the phone, so this is how one sender gets through. Off by default:
// a sender is a VIP only once the owner adds them.
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { DocumentView } from "@shared/api";
import { isApiError } from "@web/lib/api";
import { feedApi } from "@web/lib/feedApi";
import { feedKeys } from "@web/lib/feedQueries";
import { registerUndo } from "@web/lib/undoRegistry";
import {
  addVipEntries,
  removeVipAddress,
  vipMatch,
  VIPS_DOC_KEY,
  type VipMatch,
} from "@web/settings/vips";

async function fetchVipsDoc(signal?: AbortSignal): Promise<DocumentView | null> {
  try {
    return await feedApi.doc(VIPS_DOC_KEY, signal);
  } catch (error) {
    if (isApiError(error) && error.status === 404) return null;
    throw error;
  }
}

/** The VIPs document (null before the first save), shared with the VIPs page's cache. */
export function useVipsDocument() {
  return useQuery({
    queryKey: feedKeys.doc(VIPS_DOC_KEY),
    queryFn: ({ signal }) => fetchVipsDoc(signal),
    staleTime: 30_000,
  });
}

export function useVipMatch(address: string | null): VipMatch | null {
  const doc = useVipsDocument();
  if (!address || doc.isPending || doc.isError) return null;
  return vipMatch(doc.data?.content ?? "", address);
}

function newestRevisionId(doc: DocumentView): string | null {
  return (
    doc.revisions.reduce<DocumentView["revisions"][number] | null>(
      (best, r) => (!best || r.rev > best.rev ? r : best),
      null,
    )?.id ?? null
  );
}

function accept(queryClient: QueryClient, doc: DocumentView) {
  queryClient.setQueryData(feedKeys.doc(VIPS_DOC_KEY), doc);
  void queryClient.invalidateQueries({ queryKey: feedKeys.docs });
}

/** Saves `change(content)` on the newest document; one retry if it moved on meanwhile. */
async function saveChange(
  queryClient: QueryClient,
  change: (content: string) => string,
): Promise<{ saved: DocumentView; before: DocumentView | null }> {
  for (let attempt = 0; ; attempt++) {
    const before = await fetchVipsDoc();
    try {
      const saved = await feedApi.saveDoc(
        VIPS_DOC_KEY,
        change(before?.content ?? ""),
        before?.rev ?? 0,
      );
      accept(queryClient, saved);
      return { saved, before };
    } catch (error) {
      const moved = isApiError(error, "conflict") || isApiError(error, "stale");
      if (!moved || attempt > 0) throw error;
    }
  }
}

export function useSenderNotifyToggle() {
  const queryClient = useQueryClient();
  return async (sender: { address: string; label: string }, on: boolean): Promise<boolean> => {
    try {
      const { saved, before } = await saveChange(queryClient, (content) =>
        on ? addVipEntries(content, [sender.address]) : removeVipAddress(content, sender.address),
      );
      const message = on
        ? `${sender.label} is a VIP now: you'll hear when they need you`
        : `${sender.label} isn't a VIP anymore`;
      const revisionId = newestRevisionId(saved);
      if (before && revisionId)
        registerUndo({
          message,
          durationMs: 8000,
          undo: async () => accept(queryClient, await feedApi.revertDoc(VIPS_DOC_KEY, revisionId)),
          doneMessage: "VIPs restored.",
        });
      else toast(message);
      return true;
    } catch {
      toast("Couldn't change your VIPs just now. Nothing changed.");
      return false;
    }
  };
}
